import React, { FC } from 'react';
import { Bar, BarChart, ResponsiveContainer, XAxis } from 'recharts';

// Types
type ProductEntry = {
  productName: string;
  productState?: string;
  definitionStatus?: string;
  version?: string;
  deviceId: number;
  timestamp?: number;
};

type ResultType = 'CMC AV' | 'Microsoft Defender' | 'Not Detected' | 'Total';

type Props = {
  data: ProductEntry[];
  filter?: (originalName: string) => void;
};

const COLORS: Record<ResultType, string> = {
  'CMC AV': '#76EFD7',
  'Microsoft Defender': '#AFD8F7',
  'Not Detected': '#FF6666',
  Total: '#00C49F',
};

export const DeviceAVChart: FC<Props> = ({ data, filter }) => {
  const getAVSummary = (): Record<
    ResultType,
    {
      count: number;
      originalName: string;
    }
  > => {
    const summary: Record<ResultType, { count: number; originalName: string }> =
      {
        'CMC AV': { count: 0, originalName: '' },
        'Microsoft Defender': { count: 0, originalName: '' },
        'Not Detected': { count: 0, originalName: '' },
      };

    data.forEach(({ productName }) => {
      if (!productName) return;

      let category: ResultType | null = null;

      if (productName.includes('Bitdefender')) {
        category = 'CMC AV';
      } else if (productName.includes('Microsoft Defender')) {
        category = 'Microsoft Defender';
      } else if (productName === 'NONE') {
        category = 'Not Detected';
      }

      if (category) {
        summary[category].count++;
        if (!summary[category].originalName) {
          summary[category].originalName = productName;
        }
      }
    });

    return summary;
  };

  const avSummary = getAVSummary();

  const totalDevices =
    avSummary['CMC AV'].count +
    avSummary['Microsoft Defender'].count +
    avSummary['Not Detected'].count;

  const chartData = [
    {
      name: 'Devices with AV',
      'CMC AV': avSummary['CMC AV'].count,
      'Microsoft Defender': avSummary['Microsoft Defender'].count,
      'Not Detected': avSummary['Not Detected'].count,
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-[80%]">
      <div className="flex flex-col md:flex-row items-center justify-center gap-6">
        <div className="w-[50px] h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis hide dataKey="name" />
              {Object.entries(avSummary).map(([name, { originalName }]) => (
                <Bar
                  key={name}
                  dataKey={name}
                  stackId="av"
                  fill={COLORS[name as ResultType]}
                  barSize={15}
                  onClick={() => filter?.(originalName)}
                  className="cursor-pointer"
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-col justify-center max-h-[180px]">
          {Object.entries(avSummary)
            .reverse()
            .map(([name, { count, originalName }]) => {
              const percentage = ((count / totalDevices) * 100).toFixed(0);
              return (
                <div
                  key={name}
                  className="flex items-center text-sm space-x-4 mb-1 cursor-pointer"
                  onClick={() => filter?.(originalName)}
                >
                  <div
                    className="w-[15px] h-[15px]"
                    style={{ backgroundColor: COLORS[name as ResultType] }}
                  />
                  <span>
                    {name} ({count}) - {percentage}%
                  </span>
                </div>
              );
            })}
          <div className="py-1 border-b dark:border-gray-700 w-full my-3"></div>
          <div
            key={totalDevices}
            className="flex items-center text-sm space-x-4 mb-1 cursor-pointer"
            onClick={() => filter?.('')}
          >
            <div
              className="w-[15px] h-[15px]"
              style={{ backgroundColor: COLORS['Total'] }}
            />
            <span>Total: {totalDevices}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceAVChart;
